/**
 * ChatHistorySidebar - Conversation history for Burt 2.0
 * 
 * Features:
 * - List of past conversations (grouped by date)
 * - Click to resume
 * - Search
 * - New chat button
 * - Auto-titles
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Divider,
  CircularProgress,
  Badge,
  Tooltip,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material';
import {
  Add,
  Search,
  Chat,
  ExpandMore,
  ExpandLess,
  Delete,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useFamilyId } from '../contexts/FamilyIdContext';
import {
  loadChatSessions,
  groupSessionsByDate,
  deleteChatSession,
} from '../utils/burtChatUtils';

function ChatHistorySidebar({ onSelectSession, onNewChat, selectedSessionId }) {
  const { currentUser } = useAuth();
  const { familyId } = useFamilyId();
  
  const [sessions, setSessions] = useState([]);
  const [groupedSessions, setGroupedSessions] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [confirmDeleteSession, setConfirmDeleteSession] = useState({ open: false, sessionId: null });
  const [expandedGroups, setExpandedGroups] = useState({
    Today: true,
    Yesterday: true,
    'This Week': true,
    Older: true,
  });

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const loaded = await loadChatSessions(familyId, currentUser.uid, 20);
      setSessions(loaded);
      setGroupedSessions(groupSessionsByDate(loaded));
    } catch (error) {
      console.error('Failed to load chat sessions:', error);
    } finally {
      setLoading(false);
    }
  }, [familyId, currentUser?.uid]);

  useEffect(() => {
    if (familyId && currentUser?.uid) {
      loadSessions();
    }
    // loadSessions is stable (wrapped in useCallback), so we don't need it in deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyId, currentUser?.uid]);

  const handleToggleGroup = (group) => {
    setExpandedGroups(prev => ({
      ...prev,
      [group]: !prev[group],
    }));
  };

  const handleDeleteSession = (e, sessionId) => {
    e.stopPropagation();
    setConfirmDeleteSession({ open: true, sessionId });
  };

  const executeDeleteSession = async (sessionId) => {
    try {
      await deleteChatSession(familyId, sessionId);
      await loadSessions(); // Reload
    } catch (error) {
      console.error('Failed to delete session:', error);
      // Silently fail - no alert for Play Store compatibility
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate?.() || new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const filteredSessions = searchTerm
    ? sessions.filter(s => 
        s.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.messages?.some(m => m.content?.toLowerCase().includes(searchTerm.toLowerCase()))
      )
    : null;

  const displayGroups = searchTerm ? { SearchResults: filteredSessions } : groupedSessions;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRight: 1,
        borderColor: 'divider',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
          <IconButton
            onClick={onNewChat}
            color="primary"
            size="small"
            title="New Chat"
          >
            <Add />
          </IconButton>
          <TextField
            size="small"
            placeholder="Search chats..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {/* Session List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          Object.entries(displayGroups).map(([group, groupSessions]) => (
            <Box key={group}>
              <ListItem
                button
                onClick={() => handleToggleGroup(group)}
                sx={{ bgcolor: 'background.default', py: 0.5 }}
              >
                <ListItemText
                  primary={
                    <Typography variant="subtitle2" color="text.secondary">
                      {group}
                    </Typography>
                  }
                />
                {groupSessions?.length > 0 && (
                  searchTerm ? null : (
                    expandedGroups[group] ? <ExpandLess /> : <ExpandMore />
                  )
                )}
              </ListItem>
              
              <Collapse in={expandedGroups[group]} timeout="auto" unmountOnExit>
                {groupSessions?.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
                    No conversations
                  </Typography>
                ) : (
                  <List dense>
                    {groupSessions.map((session) => (
                      <ListItem
                        key={session.id}
                        button
                        selected={session.id === selectedSessionId}
                        onClick={() => onSelectSession(session)}
                        sx={{
                          '&.Mui-selected': {
                            bgcolor: 'action.selected',
                            '&:hover': {
                              bgcolor: 'action.selectedHover',
                            },
                          },
                        }}
                      >
                        <ListItemAvatar>
                          <Badge
                            overlap="circular"
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            variant="dot"
                          >
                            <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main' }}>
                              <Chat fontSize="small" />
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="body2" noWrap>
                              {session.title}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {formatTime(session.lastActiveAt)} • {session.messageCount} msgs
                            </Typography>
                          }
                        />
                        <Tooltip title="Delete">
                          <IconButton
                            onClick={(e) => handleDeleteSession(e, session.id)}
                            sx={{ ml: 1, minWidth: 48, minHeight: 48 }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Collapse>
              
              {!searchTerm && <Divider />}
            </Box>
          ))
        )}
      </Box>

      {/* Confirm Delete Session Dialog */}
      <Dialog open={confirmDeleteSession.open} onClose={() => setConfirmDeleteSession({ open: false, sessionId: null })} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Conversation?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this conversation? This cannot be undone.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteSession({ open: false, sessionId: null })} sx={{ minHeight: 48 }}>Cancel</Button>
          <Button variant="contained" color="error" sx={{ minHeight: 48 }} onClick={() => {
            const { sessionId } = confirmDeleteSession;
            setConfirmDeleteSession({ open: false, sessionId: null });
            executeDeleteSession(sessionId);
          }}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChatHistorySidebar;
